<template>
    <div>
        <NavbarOne/>

        <!-- data-strength is the layer's maximum travel in px. The spread between them is
             what reads as depth: the lamp drifts three times as far as the chair, so the eye
             places the lamp nearer. A negative value drifts against the cursor, pushing the
             background blob further back still. See onHeroMove() below. -->
        <div
            ref="hero"
            @mousemove="onHeroMove"
            @mouseleave="onHeroLeave"
            class="pt-36 sm:pt-52 lg:pt-[250px] pb-12 sm:pb-16 lg:pb-36 2xl:py-[360px] bg-[#f5f5f5] dark:bg-dark-secondary relative z-[1] overflow-hidden"
        >
            <div class="absolute -z-[1] top-0 left-0 w-[60%] 2xl:w-[955px] px-layer" data-strength="-30">
            <svg class="fill-current text-[#BB976D] dark:text-title w-[100%]" viewBox="0 0 955 780" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.1" fill-rule="evenodd" clip-rule="evenodd" d="M830.97 723.759C971.43 620.07 992.216 359.628 893.195 163.09C862.083 101.856 819.328 46.7521 768.467 0H0V470.225C59.4825 536.72 135.112 598.064 218.046 647.736C429.653 775.193 690.606 828.224 830.97 723.759Z"/>
            </svg>
            </div>
            <div class="absolute bottom-0 left-0 -z-[1] hidden 2xl:block px-layer" data-strength="60">
                <img :src="shape1" alt="banner-shape">
            </div>
            <!-- px-centered keeps Tailwind's -translate-x-1/2 centering, which a plain
                 transform on this element would otherwise wipe out (the lamp would jump
                 half its width to the right the moment the cursor entered). -->
            <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -z-[1] w-[17%] hidden lg:block px-layer px-centered" data-strength="90">
                <img :src="shape2" alt="banner-shape">
            </div>
            <div class="absolute bottom-0 right-0 z-[1] w-[40%] md:w-40 lg:w-64 2xl:w-auto px-layer" data-strength="55">
                <img :src="shape3" alt="banner-shape">
            </div>
            <div class="container">
                <div class="max-w-1366 mx-auto flex flex-col gap-10">
                    <!-- Copy comes from the admin (Website & App > Home & About Text).
                         Until the request answers, site-content.js serves a bundled
                         fallback, so the hero never paints empty. -->
                    <div>
                        <h4 v-if="heroCopy.eyebrow" class="leading-none font-medium dark:text-white" data-aos="fade-up">{{ heroCopy.eyebrow }}</h4>
                        <h2 class="leading-none text-4xl sm:text-5xl xl:text-6xl 2xl:text-7xl font-bold mt-6 dark:text-white" data-aos="fade-up" data-aos-delay="100">
                            <template v-for="(part, i) in headlineParts" :key="i">
                                <span :class="part.highlighted ? 'text-primary' : ''">{{ part.text }}</span>
                            </template>
                        </h2>
                        <p v-if="heroCopy.description" class="mt-4 md:mt-5 dark:text-white-light md:max-w-[350px] lg:max-w-xl" data-aos="fade-up" data-aos-delay="200">{{ heroCopy.description }}</p>
                        <div class="button" data-aos="fade-up" data-aos-delay="300">
                            <router-link class="btn btn-outline mt-6" :to="heroCopy.button_link" :data-text="heroCopy.button_label"><span>{{ heroCopy.button_label }}</span></router-link>
                        </div>
                    </div>
                    <!-- The heaviest layer, so the smallest travel and the longest settle. -->
                    <div class="relative md:absolute bottom-0 right-0 2xl:right-24 w-full md:max-w-[400px] lg:max-w-[40%] 2xl:w-auto px-layer" data-strength="32">
                        <img :src="shape4" alt="thumb">
                    </div>
                </div>
            </div>

            <router-link to="" class="w-7 lg:w-9 h-12 lg:h-[60px] rounded-[69px] border-[1.5px] border-title dark:border-white text-title dark:text-white flex items-center justify-center absolute bottom-7 md:bottom-12 left-1/2 transform -translate-x-1/2" id="scroll-button">
                <svg class="fill-current scroll-down-icon" width="12" height="22" viewBox="0 0 12 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.46967 21.0603C5.76256 21.3532 6.23744 21.3532 6.53033 21.0603L11.3033 16.2874C11.5962 15.9945 11.5962 15.5196 11.3033 15.2267C11.0104 14.9338 10.5355 14.9338 10.2426 15.2267L6 19.4693L1.75736 15.2267C1.46447 14.9338 0.989593 14.9338 0.6967 15.2267C0.403806 15.5196 0.403806 15.9945 0.6967 16.2874L5.46967 21.0603ZM5.25 0.832031L5.25 20.53L6.75 20.53L6.75 0.832031L5.25 0.832031Z"/>
                </svg>
            </router-link>
        </div>

        <div class="s-py-100-50" id="sec-2">
            <div class="container">
                <div class="max-w-[1365px] mx-auto flex 2xl:items-center gap-[30px] md:gap-10 justify-between flex-col 2xl:flex-row">
                    <div class="2xl:max-w-[360px] w-full flex justify-between 2xl:justify-start flex-wrap items-end 2xl:items-start gap-5 2xl:gap-0 flex-row 2xl:flex-col" data-aos="fade-up">
                        <div>
                            <img :src="chair" alt="" class="size-16"/>
                            <h2 class="mt-[15px] leading-none">Popular Collection</h2>
                            <p class="mt-[10px] md:mt-[15px]">Explore our curated popular collection product categories featuring high-quality furniture. </p>
                        </div>
                        <router-link class="btn btn-outline 2xl:mt-6" to="/shop-v2" data-text="See all Productss"><span>See all Productss</span></router-link>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-6 lg::gap-8 2xl:max-w-[903px] w-full" data-aos="fade-up" data-aos-delay="100">
                        <LayoutOne
                            :productList="popularCollection"
                            :classList="'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-6 lg:gap-8 md:col-span-3'"
                        />
                    </div>
                </div>
            </div>
        </div>


        <!-- Hidden outright when the back office has no active categories: an empty
             carousel is a band of blank page, worse than no section at all. The live
             catalogue starts empty, so this is the first state the section ships in. -->
        <div v-if="categoriesLoading || categorySlides.length" class="s-py-50 overflow-hidden" data-aos="fade-up">
            <div v-if="categoriesLoading" class="container">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
                    <div v-for="n in 4" :key="n" class="relative aspect-[4/3] bg-[#f5f5f5] dark:bg-dark-secondary animate-pulse"></div>
                </div>
            </div>
            <Swiper
                v-else
                :modules="[Autoplay]"
                :slides-per-view="4"
                :space-between="0"
                :loop="categorySlides.length > 4"
                :autoplay="{ delay: 3000}"
                :breakpoints="{
                    320: { slidesPerView: 1, spaceBetween: 0 },
                    576: { slidesPerView: 2, spaceBetween: 0 },
                    1024: { slidesPerView: 4, spaceBetween: 0 },
                }"
             class="owl-carousel blog-v1-wrapper block cursor-pointer">
                <SwiperSlide v-for="item in categorySlides" :key="item.key" class="relative">
                    <router-link :to="item.link" class="block relative group overflow-hidden" :aria-label="`Shop ${item.name}`">
                        <img class="w-full aspect-[4/3] object-cover transition duration-500 group-hover:scale-105" :src="item.image" :alt="item.name" @error="onCategoryImageError($event, item)"/>
                        <div class="absolute bottom-7 left-0 px-5 transform w-full flex justify-center">
                            <div class="min-w-[250px] bg-white bg-opacity-80 dark:bg-title dark:bg-opacity-80 p-5 z-10 transition duration-300 group-hover:bg-opacity-100">
                                <h4 class="leading-[1.5] font-semibold">{{item.name}}</h4>
                                <p class="leading-none mt-[10px]">{{item.productLabel}}</p>
                            </div>
                        </div>
                    </router-link>
                </SwiperSlide>
            </Swiper>
        </div>

        <div class="s-py-50-100">
            <div class="container max-w-[1365px] mx-auto">
                <div class="max-w-[547px] mx-auto mb-8 md:mb-12 text-center" data-aos="fade-up">
                    <img :src="bed" alt="" class="mx-auto w-14 sm:w-[70px]"/>
                        
                    <h2 class="mt-[15px] leading-none">New Arrival</h2>
                    <p class="mt-[10px] md:mt-[15px]">Discover the latest must-have arrivals! Elevate your style with our newest collection of trendsetting items. </p>
                </div>
                <div v-if="homeLoading" class="py-12 text-center text-base text-paragraph dark:text-white-light">
                    Loading collections...
                </div>
                <div v-else class="grid gap-[30px]" data-aos="fade-up" data-aos-delay="100">
                    <LayoutOne
                        :productList="newArrivalCollection"
                        :classList="'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-[30px]'"
                    />
                </div>
                <div class="text-center mt-7 md:mt-12">
                    <router-link to="/shop-v2" class="btn btn-outline" data-text="Go to shop">
                        <span>Go to shop</span>
                    </router-link>
                </div>
            </div>
        </div>

        <ServicesSection />

        <div class="s-py-50-100">
            <div class="container-fluid">
                <div class="max-w-[1720px] mx-auto">
                    <div class="max-w-xl mx-auto mb-8 md:mb-12 text-center" data-aos="fade-up">
                        <h3 class="leading-none mt-4 md:mt-6 text-2xl md:text-3xl">Our Portfolio</h3>
                        <p class="mt-3">A glimpse of recent projects we're proud of. See craftsmanship in real spaces.</p>
                    </div>

                    <p v-if="portfolioLoading" class="text-center text-paragraph dark:text-white-light">Loading...</p>
                    <div v-else-if="homePortfolios.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8" data-aos="fade-up" data-aos-delay="100">
                        <router-link
                            v-for="item in homePortfolios"
                            :key="item.id"
                            :to="`/portfolio-details-v1/${item.id}`"
                            class="relative group overflow-hidden block aspect-[4/3] sm:aspect-[4/5] bg-[#F6F2EC] rounded-[6px]"
                        >
                            <img class="w-full h-full object-cover transition duration-500 group-hover:scale-105 group-hover:opacity-45" :src="item.main_image_url || portfolioFallback" alt="Portfolio"/>
                            <div class="absolute inset-0 bg-black/0 transition duration-500 group-hover:bg-black/65"></div>
                            <div class="absolute inset-0 bg-gradient-to-t from-black/95 via-black/65 to-black/20 opacity-0 transition duration-500 group-hover:opacity-100"></div>
                            <div class="absolute inset-x-0 bottom-0 z-10 p-4 sm:p-6 md:p-7">
                                <div class="translate-y-3 sm:translate-y-5 opacity-95 transition duration-500 group-hover:translate-y-0">
                                    <span v-if="item.category" class="inline-block text-[12px] sm:text-[13px] leading-none text-title font-medium px-2.5 py-1.5 sm:px-3 sm:py-2 bg-[#DBCBBD] rounded-md">
                                    {{ item.category }}
                                    </span>
                                    <span class="block text-base sm:text-lg md:text-xl font-semibold leading-tight text-white mt-2 sm:mt-3">
                                        {{ item.title }}
                                    </span>
                                </div>
                                <p
                                    v-if="item.short_description"
                                    class="mt-2 sm:mt-3 max-w-[28ch] text-[13px] sm:text-sm leading-5 sm:leading-6 text-white/85 overflow-hidden max-h-0 opacity-0 translate-y-3 transition-all duration-500 group-hover:max-h-24 group-hover:opacity-100 group-hover:translate-y-0"
                                >
                                    {{ item.short_description }}
                                </p>
                            </div>
                        </router-link>
                    </div>
                    <p v-else class="text-center text-paragraph dark:text-white-light">No portfolios published yet.</p>

                    <div class="mt-8 md:mt-12 text-center" data-aos="fade-up" data-aos-delay="200">
                        <router-link to="/portfolio-v1" class="btn btn-outline" data-text="View More">
                            <span>View More</span>
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <div class="s-py-50-100">
            <NewsLetterOne/>
        </div>

        <div v-if="stats.length" class="bg-primary-light px-5 py-7 md:py-12 dark:bg-tertiary dark:bg-opacity-20" data-aos="fade-up">
            <div class="container">
                <StatCounters :stats="stats"/>
            </div>
        </div>

        <FooterTwo/>

        <ScrollToTop/>

    </div>
</template>

<script setup>
    import { computed, onMounted, onUnmounted, ref } from 'vue';

    import shape1 from '@/assets/img/home-v2/banner-shape-01.png'
    import shape2 from '@/assets/img/home-v2/banner-shape-02.png'
    import shape3 from '@/assets/img/home-v2/banner-shape-03.png'
    import shape4 from '@/assets/img/home-v2/banner-shape-04.png'
    import bed from '@/assets/img/svg/bed.svg'
    import chair from '@/assets/img/svg/chair.svg'
    
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import LayoutOne from '@/components/product/layout-one.vue';
    import NewsLetterOne from '@/components/news/news-letter-one.vue';
    import StatCounters from '@/components/stat-counters.vue';
import ServicesSection from '@/components/services/services-section.vue';
    import FooterTwo from '@/components/footer/footer-two.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    
    import Aos from 'aos';
    import { mapCategory, mapProductCard, shopCategoryLink, storefrontRequest } from '@/lib/storefront-api';
    import { splitHighlight, useSiteContent } from '@/lib/site-content';
    import portfolioFallback from '@/assets/img/gallery/shop-01/shop-01.jpg'

    // Shown only for a category the back office has not been given a picture for yet,
    // so a new category never renders as a hole in the carousel.
    import categoryFallback1 from '@/assets/img/home-v2/product/pdct-01.jpg'
    import categoryFallback2 from '@/assets/img/home-v2/product/pdct-02.jpg'
    import categoryFallback3 from '@/assets/img/home-v2/product/pdct-03.jpg'
    import categoryFallback4 from '@/assets/img/home-v2/product/pdct-04.jpg'

    const CATEGORY_FALLBACK_IMAGES = [categoryFallback1, categoryFallback2, categoryFallback3, categoryFallback4];


    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { Autoplay } from 'swiper/modules';
    import 'swiper/swiper-bundle.css';

    // Admin-editable hero copy. useSiteContent() shares one request with any
    // other page that asks, and serves a bundled fallback until it answers.
    const { content: siteContent } = useSiteContent();
    const heroCopy = computed(() => siteContent.value.hero);
    const stats = computed(() => siteContent.value.stats);
    const headlineParts = computed(() => splitHighlight(heroCopy.value.title, heroCopy.value.title_highlight));

    const popularCollection = ref([]);
    const newArrivalCollection = ref([]);
    const homeLoading = ref(true);
    const homePortfolios = ref([]);
    const portfolioLoading = ref(true);
    const categories = ref([]);
    const categoriesLoading = ref(true);

    /**
     * The carousel tiles: one per active top-level category from the back office,
     * with the picture and the product count it holds there.
     *
     * Swiper's loop mode needs more slides than it shows at once or it stalls with a
     * gap, and the shop has five categories against four visible columns — which is
     * only just enough, and stops being enough the moment a category is deactivated.
     * Duplicating the list when it is short keeps the loop full without inventing
     * categories that do not exist; :key carries the copy index so Vue does not see
     * two nodes claiming the same identity.
     */
    const categorySlides = computed(() => {
        const source = categories.value;

        if (!source.length) {
            return [];
        }

        const copies = source.length > 4 ? 1 : 2;
        const slides = [];

        for (let copy = 0; copy < copies; copy += 1) {
            source.forEach((category, index) => {
                const fallback = CATEGORY_FALLBACK_IMAGES[index % CATEGORY_FALLBACK_IMAGES.length];

                slides.push({
                    key: `${category.id}-${copy}`,
                    name: category.name,
                    image: category.image || fallback,
                    fallback,
                    productLabel: `${category.productCount} ${category.productCount === 1 ? 'Product' : 'Products'}`,
                    link: shopCategoryLink(category),
                });
            });
        }

        return slides;
    });

    /* ---------------------------------------------------------------------------
     * Hero parallax
     *
     * The banner is already four transparent PNGs on their own absolutely
     * positioned layers, so depth costs nothing but a transform: read the cursor
     * as a -1..+1 offset from the centre of the hero, multiply by each layer's
     * data-strength, and hand the result to CSS as --px/--py. Layers move by
     * different amounts, and the eye reads "different speed" as "different
     * distance" — the same reason fence posts fly past a car window and the
     * mountains behind them barely shift.
     *
     * The transform is never written directly. --px/--py are custom properties so
     * the stylesheet can compose the drift with a layer's existing translate
     * (see .px-centered) instead of overwriting it.
     * ------------------------------------------------------------------------ */
    const hero = ref(null);
    let heroLayers = [];
    let heroFrame = 0;
    let heroTarget = null;

    // A pointer fires mousemove far faster than the screen refreshes. Writing a
    // style per event means style recalcs the browser throws away; coalescing into
    // one rAF means at most one write per painted frame.
    function paintHero() {
        heroFrame = 0;

        if (!heroTarget) {
            return;
        }

        const { x, y } = heroTarget;

        for (const layer of heroLayers) {
            const strength = Number(layer.dataset.strength || 0);
            layer.style.setProperty('--px', `${(x * strength).toFixed(2)}px`);
            // Damped vertically: the hero is far wider than it is tall, so equal
            // travel on both axes reads as a wobble rather than a drift.
            layer.style.setProperty('--py', `${(y * strength * 0.55).toFixed(2)}px`);
        }
    }

    function onHeroMove(event) {
        if (!heroLayers.length) {
            return;
        }

        const rect = hero.value.getBoundingClientRect();

        heroTarget = {
            x: (event.clientX - rect.left - rect.width / 2) / (rect.width / 2),
            y: (event.clientY - rect.top - rect.height / 2) / (rect.height / 2),
        };

        if (!heroFrame) {
            heroFrame = requestAnimationFrame(paintHero);
        }
    }

    // Without this the layers stay frozen wherever the cursor left the hero.
    function onHeroLeave() {
        heroTarget = null;

        if (heroFrame) {
            cancelAnimationFrame(heroFrame);
            heroFrame = 0;
        }

        for (const layer of heroLayers) {
            layer.style.setProperty('--px', '0px');
            layer.style.setProperty('--py', '0px');
        }
    }

    function startHeroParallax() {
        // A finger has no hover state, and a visitor who asked for reduced motion
        // asked for exactly this. In both cases heroLayers stays empty and the
        // handlers above return on their first line.
        const pointerHovers = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        const wantsMotion = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!hero.value || !pointerHovers || !wantsMotion) {
            return;
        }

        heroLayers = Array.from(hero.value.querySelectorAll('[data-strength]'));

        for (const layer of heroLayers) {
            // Heavy things settle slowly. Travel and duration run in opposite
            // directions, so the layers don't just move different distances —
            // they arrive at different times, which is what sells the weight.
            // The x5 slope is tuned to the strengths in the template (30-90): steep
            // enough to spread the arrivals, shallow enough that the fastest layer
            // does not slam into the 850ms floor and flatten the difference.
            const strength = Math.abs(Number(layer.dataset.strength || 0));
            layer.style.setProperty('--dur', `${Math.min(1700, Math.max(850, 1700 - strength * 5))}ms`);
        }
    }

    /**
     * A category can carry an image path the back office saved but whose file is no
     * longer on disk — the API has no cheap way to know that, and the visitor would
     * otherwise get a broken-image icon where the tile art should be. Swapping to the
     * same placeholder an image-less category gets keeps the strip whole.
     *
     * Guarded against a second error: if the placeholder itself failed there is
     * nothing left to try, and reassigning src would loop.
     */
    function onCategoryImageError(event, item) {
        if (event.target.src.endsWith(item.fallback)) {
            return;
        }

        event.target.src = item.fallback;
    }

    async function loadCategories() {
        categoriesLoading.value = true;

        try {
            const response = await storefrontRequest('/categories');
            categories.value = (response.data || []).map(mapCategory);
        } catch (error) {
            categories.value = [];
        } finally {
            categoriesLoading.value = false;
        }
    }

    async function loadHomePortfolios() {
        try {
            const response = await storefrontRequest('/portfolios', { params: { per_page: 3, featured: true } });
            let items = response.data || [];
            if (items.length < 3) {
                const fallback = await storefrontRequest('/portfolios', { params: { per_page: 3 } });
                items = fallback.data || [];
            }
            homePortfolios.value = items.slice(0, 3);
        } catch (e) {
            homePortfolios.value = [];
        } finally {
            portfolioLoading.value = false;
        }
    }

    async function loadHomeCollections() {
        homeLoading.value = true;

        try {
            const [popularResponse, arrivalResponse] = await Promise.all([
                storefrontRequest('/products', {
                    params: {
                        popular_collection: true,
                        per_page: 3,
                    },
                }),
                storefrontRequest('/products', {
                    params: {
                        new_arrival: true,
                        per_page: 4,
                    },
                }),
            ]);

            popularCollection.value = (popularResponse.data || []).map(mapProductCard);
            newArrivalCollection.value = (arrivalResponse.data || []).map(mapProductCard);
        } catch (error) {
            popularCollection.value = [];
            newArrivalCollection.value = [];
        } finally {
            homeLoading.value = false;
        }
    }

    onMounted(async ()=>{
        Aos.init()
        // Before the awaits: the banner layers are static markup and are already in
        // the DOM, so there is no reason to make the hero wait on the product fetch.
        startHeroParallax();
        // Independent endpoints: the category strip must not wait behind the product
        // grid before its first tile can appear.
        loadCategories();
        await loadHomeCollections();
        loadHomePortfolios();
    })

    onUnmounted(() => {
        if (heroFrame) {
            cancelAnimationFrame(heroFrame);
            heroFrame = 0;
        }
        heroLayers = [];
    })
</script>

<style scoped>
    /* [data-strength] is here to raise specificity above Tailwind's single-class
       utilities. Without it .px-layer and Tailwind's .transform tie at 0-1-0 and the
       winner comes down to stylesheet order, which the build controls, not us. */
    .px-layer[data-strength] {
        transform: translate3d(var(--px, 0px), var(--py, 0px), 0);
        transition: transform var(--dur, 1400ms) cubic-bezier(0.22, 0.61, 0.36, 1);
        will-change: transform;
    }

    /* Composes the drift on top of the centering rather than replacing it. */
    .px-layer[data-strength].px-centered {
        transform: translateX(-50%) translate3d(var(--px, 0px), var(--py, 0px), 0);
    }

    /* Belt and braces: startHeroParallax() already declines to bind, but this keeps
       the layers pinned even if a stray --px were ever set. */
    @media (prefers-reduced-motion: reduce) {
        .px-layer[data-strength] {
            --px: 0px;
            --py: 0px;
            transition: none;
        }
    }
</style>
